import { useEffect, useMemo, useState } from "react";
import { Offcanvas } from "react-bootstrap";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Spinner } from "reactstrap";
import useDataFlowOptions from "../hooks/useDataFlowOptions";
import useUpdateDataFlow from "../hooks/useUpdateDataFlow";
import {
    DIRECTION_RECEIVES,
    buildCreatePayload,
    getEndpointFieldLabels,
    isHardwareCounterpart,
    isReceivesFromHardware,
    resolveHardwareRelation,
    toOptionValues,
    toSelectOptions,
    toSelectedOptions,
    withRelationOptionAvailability,
} from "../helpers/flowForm";
import CounterpartPicker from "./CounterpartPicker";
import DataFlowFormFields from "./DataFlowFormFields";

const EMPTY_FORM = {
    selectedCounterpart: null,
    purpose: "",
    notes: "",
    identifier: "",
    dataTypes: [],
    dataCategories: [],
    methods: [],
    relation: null,
};

const EditDataFlowOffcanvas = ({
    isOpen,
    card,
    softwareId,
    wrapperAssetId,
    onClose,
    t,
}) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});

    const { data: options = {} } = useDataFlowOptions({ t });
    const { updateFlow, isSaving } = useUpdateDataFlow({
        softwareId,
        t,
        onSuccess: onClose,
        onError: setErrors,
    });

    const dataTypeOptions = useMemo(
        () => toSelectOptions(options.data_types, t),
        [options.data_types, t]
    );
    const dataCategoryOptions = useMemo(
        () => toSelectOptions(options.data_categories, t),
        [options.data_categories, t]
    );
    const methodOptions = useMemo(
        () => toSelectOptions(options.transfer_methods, t),
        [options.transfer_methods, t]
    );
    const relationOptions = useMemo(
        () => toSelectOptions(options.relations, t),
        [options.relations, t]
    );
    const restrictRelationToSyncs = isReceivesFromHardware(
        card?.direction,
        form.selectedCounterpart
    );
    const visibleRelationOptions = useMemo(
        () =>
            withRelationOptionAvailability(relationOptions, {
                onlySyncs: restrictRelationToSyncs,
            }),
        [relationOptions, restrictRelationToSyncs]
    );

    useEffect(() => {
        if (!isOpen || !card) {
            setForm(EMPTY_FORM);
            setErrors({});
            return;
        }

        const selectedCounterpart = card.counterpart || null;
        const currentRelation =
            relationOptions.find((option) => option.value === card.relation) ||
            null;

        setForm({
            selectedCounterpart,
            purpose: card.purpose || "",
            notes: card.notes || "",
            identifier: card.counterpart?.identifier || "",
            dataTypes: toSelectedOptions(card.dataTypes, dataTypeOptions),
            dataCategories: toSelectedOptions(
                card.dataCategories,
                dataCategoryOptions
            ),
            methods: toSelectedOptions(card.methods, methodOptions),
            relation: resolveHardwareRelation(
                card.direction,
                selectedCounterpart,
                currentRelation,
                relationOptions
            ),
        });
    }, [
        card,
        dataCategoryOptions,
        dataTypeOptions,
        isOpen,
        methodOptions,
        relationOptions,
    ]);

    const updateForm = (patch) => {
        setForm((current) => ({
            ...current,
            ...patch,
        }));
    };

    const handleClose = () => {
        if (isSaving) {
            return;
        }

        onClose();
    };

    const handleSave = () => {
        if (!wrapperAssetId || !card?.id) {
            toast(t("Failed to update data flow."), {
                type: "error",
            });
            return;
        }

        if (!form.selectedCounterpart) {
            const field =
                card.direction === DIRECTION_RECEIVES ? "source" : "destination";

            setErrors((current) => ({
                ...current,
                [field]: t("A valid asset or endpoint reference is required"),
            }));
            return;
        }

        const counterpart = form.selectedCounterpart;
        const nextIdentifier = String(form.identifier ?? "").trim() || null;
        const shouldUpdateEndpoint =
            counterpart.kind === "endpoint" &&
            (counterpart.identifier || null) !== nextIdentifier;

        try {
        updateFlow({
            id: card.id,
            payload: buildCreatePayload({
                direction: card.direction,
                wrapperAssetId,
                counterpart,
                purpose: form.purpose,
                notes: form.notes,
                dataTypes: toOptionValues(form.dataTypes),
                dataCategories: toOptionValues(form.dataCategories),
                methods: toOptionValues(form.methods),
                relation: isHardwareCounterpart(counterpart)
                    ? form.relation?.value || null
                    : null,
            }),
            endpointUpdate: shouldUpdateEndpoint
                ? {
                      id: counterpart.id,
                      type: counterpart.type,
                      name: counterpart.name,
                      identifier: nextIdentifier,
                  }
                : null,
        });
        } catch (error) {
            if (process.env.NODE_ENV === "development") {
                console.error(error);
            }

            toast(t("Failed to update data flow."), {
                type: "error",
            });
        }
    };

    const isReceives = card?.direction === DIRECTION_RECEIVES;
    const isEndpoint = form.selectedCounterpart?.kind === "endpoint";
    const identifierLabels = getEndpointFieldLabels(
        form.selectedCounterpart?.type
    );

    return (
        <Offcanvas
            show={isOpen}
            onHide={handleClose}
            placement="end"
            className="data-flow-v2-offcanvas"
        >
            <Offcanvas.Header closeButton={!isSaving}>
                <Offcanvas.Title>
                    <div>{t("Edit Data Flow")}</div>
                    <small className="text-muted">
                        {isReceives
                            ? t("Receives Data From")
                            : t("Sends Data To")}
                        {form.relation?.label
                            ? ` · ${form.relation.label}`
                            : ""}
                    </small>
                </Offcanvas.Title>
            </Offcanvas.Header>

            <Offcanvas.Body>
                <CounterpartPicker
                    key={isOpen ? card?.id : "closed"}
                    wrapperAssetId={wrapperAssetId}
                    value={form.selectedCounterpart}
                    onChange={(selectedCounterpart) => {
                        updateForm({
                            selectedCounterpart,
                            identifier: selectedCounterpart?.identifier || "",
                            relation: resolveHardwareRelation(
                                card?.direction,
                                selectedCounterpart,
                                form.relation,
                                relationOptions
                            ),
                        });
                        setErrors((current) => ({
                            ...current,
                            source: null,
                            destination: null,
                            relation: null,
                        }));
                    }}
                    typeLabel={isReceives ? t("Source") : t("Destination")}
                    required
                    error={errors.source || errors.destination}
                    disabled={isSaving}
                    enabled={isOpen}
                />

                <DataFlowFormFields
                    purpose={form.purpose}
                    notes={form.notes}
                    dataTypes={form.dataTypes}
                    dataCategories={form.dataCategories}
                    methods={form.methods}
                    identifier={form.identifier}
                    identifierLabel={identifierLabels.identifier}
                    identifierOptional={identifierLabels.identifierOptional}
                    showIdentifier={isEndpoint}
                    dataTypeOptions={dataTypeOptions}
                    dataCategoryOptions={dataCategoryOptions}
                    methodOptions={methodOptions}
                    errors={errors}
                    disabled={isSaving}
                    onPurposeChange={(purpose) => updateForm({ purpose })}
                    onNotesChange={(notes) => updateForm({ notes })}
                    onIdentifierChange={(identifier) =>
                        updateForm({ identifier })
                    }
                    onDataTypesChange={(dataTypes) => updateForm({ dataTypes })}
                    onDataCategoriesChange={(dataCategories) =>
                        updateForm({ dataCategories })
                    }
                    onMethodsChange={(methods) => updateForm({ methods })}
                    relation={form.relation}
                    relationOptions={visibleRelationOptions}
                    showRelation={isHardwareCounterpart(form.selectedCounterpart)}
                    onRelationChange={(relation) => updateForm({ relation })}
                />
            </Offcanvas.Body>

            <div className="data-flow-v2-offcanvas__footer">
                <Button
                    color="secondary"
                    outline
                    type="button"
                    onClick={handleClose}
                    disabled={isSaving}
                >
                    {t("Cancel")}
                </Button>
                <Button
                    color="primary"
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving}
                >
                    {isSaving ? <Spinner size="sm" /> : t("Save Flow")}
                </Button>
            </div>
        </Offcanvas>
    );
};

export default withNamespaces()(EditDataFlowOffcanvas);
